import { getCookie, removeCookie } from "../CookieHelper";
import { COOKIE_ENUM, LocalStorageEnum } from "../enums";
import LS from "../LocalStorage";
import { PromoPopupType } from "./promoMaterialTypes";

/**
 * This device's half of the pop-up display ledger (story #4087).
 *
 * The server owns every *durable* fact — an admin-authored material shown
 * once-ever, the download-app cooldown of a signed-in customer, a tier movement
 * retired by its echo. What it deliberately does not keep is the
 * session-throttled families (REWARD, ALL_REWARDS, OFFER_UNAVAILABLE): those are
 * remembered here, for guests and signed-in customers alike, and handed back on
 * every pop-ups request as the `shown` query param the server merges with its
 * own rows.
 *
 * Storage is **localStorage, not a cookie**, on purpose: `CookieHelper` writes on
 * the shared `.prepit.app` domain, so a pop-up seen on one restaurant would
 * silently suppress another restaurant's pop-up. localStorage is per-origin,
 * i.e. per restaurant, which is exactly the scope this ledger means.
 *
 * Stored — and sent — as `TYPE:epochSeconds` pairs joined by commas, one entry
 * per type, e.g. `REWARD:1787000000,ALL_REWARDS:1787000100`.
 */

/** The server's session window: how long a session-throttled display counts. */
export const SESSION_RETENTION_HOURS = 2;

/**
 * Ceiling — and default — for a DOWNLOAD_APP entry. Its meaning is a
 * merchant-configured cooldown that outlives a session, and a guest's
 * download-app display is remembered *nowhere else*, so pruning it on the
 * 2-hour session window would re-nudge the same guest the same afternoon.
 */
export const DOWNLOAD_APP_MAX_RETENTION_HOURS = 24 * 7;

const HOUR_IN_SECONDS = 60 * 60;

/**
 * The families this ledger speaks for, and how long each counts — only the ones
 * the server *cannot* remember on its own.
 *
 * ANNOUNCEMENT is deliberately absent. Its rule does open with a type-level
 * session check, but that check is satisfied by the material display row this
 * client POSTs, and the only announcement rung a guest could reach is
 * ORDER_CONFIRMATION — which no guest ever sees, because completing an order
 * requires signing in. So an announcement is always seen by someone the server
 * can key a row to, and a hint would only duplicate it.
 *
 * TIER_MOVEMENT is absent for a different reason: it is retired by echoing its
 * `tierHistoryId`, and the server decides from the movement row itself, so a
 * type-level hint could not narrow anything.
 */
const RETENTION_HOURS: Partial<Record<PromoPopupType, number>> = {
  [PromoPopupType.REWARD]: SESSION_RETENTION_HOURS,
  [PromoPopupType.ALL_REWARDS]: SESSION_RETENTION_HOURS,
  [PromoPopupType.OFFER_UNAVAILABLE]: SESSION_RETENTION_HOURS,
  [PromoPopupType.DOWNLOAD_APP]: DOWNLOAD_APP_MAX_RETENTION_HOURS,
};

type PromoPopupHistory = Partial<Record<PromoPopupType, number>>;

const isBrowser = () => typeof window !== "undefined";

const nowInSeconds = () => Math.floor(Date.now() / 1000);

const isTrackedType = (value: string): value is PromoPopupType =>
  Object.prototype.hasOwnProperty.call(RETENTION_HOURS, value);

/**
 * How long an entry of this type stays meaningful. The caller may shorten the
 * download-app window to the restaurant's own cooldown; it can never lengthen it
 * past the ceiling.
 */
const retentionSecondsFor = (
  type: PromoPopupType,
  downloadAppCooldownHours: number
) => {
  if (type === PromoPopupType.DOWNLOAD_APP) {
    const hours = Number.isFinite(downloadAppCooldownHours)
      ? Math.max(0, downloadAppCooldownHours)
      : DOWNLOAD_APP_MAX_RETENTION_HOURS;

    return Math.min(hours, DOWNLOAD_APP_MAX_RETENTION_HOURS) * HOUR_IN_SECONDS;
  }

  return (RETENTION_HOURS[type] ?? SESSION_RETENTION_HOURS) * HOUR_IN_SECONDS;
};

/** Parses one `TYPE:epochSeconds` pair; anything malformed yields nothing. */
const parseEntry = (raw: string): [PromoPopupType, number] | undefined => {
  const parts = raw.split(":");

  if (parts.length !== 2) {
    return undefined;
  }
  const [type, seconds] = parts;

  if (!isTrackedType(type)) {
    return undefined;
  }
  const recordedAt = Number(seconds);

  if (!Number.isInteger(recordedAt) || recordedAt <= 0) {
    return undefined;
  }

  return [type, recordedAt];
};

/**
 * Reads the ledger, dropping expired entries and silently discarding anything
 * unknown or malformed. Never throws: storage may be blocked, over quota or
 * absent altogether (SSR), and none of that may break a render.
 */
const readHistory = (
  downloadAppCooldownHours = DOWNLOAD_APP_MAX_RETENTION_HOURS
): PromoPopupHistory => {
  if (!isBrowser()) {
    return {};
  }
  try {
    const stored = LS._get(LocalStorageEnum.PROMO_POPUP_HISTORY);

    if (typeof stored !== "string" || stored.length === 0) {
      return {};
    }
    const now = nowInSeconds();

    return stored.split(",").reduce<PromoPopupHistory>((history, raw) => {
      const entry = parseEntry(raw.trim());

      if (!entry) {
        return history;
      }
      const [type, recordedAt] = entry;
      const age = now - recordedAt;

      // An entry exactly at the window has lapsed: the window is exclusive.
      if (age >= retentionSecondsFor(type, downloadAppCooldownHours)) {
        return history;
      }
      // One entry per type — a duplicate in storage loses to the later display.
      const previous = history[type];

      return previous !== undefined && previous > recordedAt
        ? history
        : { ...history, [type]: recordedAt };
    }, {});
  } catch (error) {
    console.log("error reading promo pop-up history", error);

    return {};
  }
};

const serialize = (history: PromoPopupHistory) =>
  (Object.keys(history) as PromoPopupType[])
    .sort((a, b) => (history[a] ?? 0) - (history[b] ?? 0) || a.localeCompare(b))
    .map((type) => `${type}:${history[type]}`)
    .join(",");

const writeHistory = (history: PromoPopupHistory) => {
  if (!isBrowser()) {
    return;
  }
  try {
    LS._set(LocalStorageEnum.PROMO_POPUP_HISTORY, serialize(history));
  } catch (error) {
    console.log("error writing promo pop-up history", error);
  }
};

/**
 * Records that this pop-up just became visible, replacing any earlier entry of
 * the same type — one entry per type, latest display wins. A family the server
 * remembers itself is not stored.
 */
export const recordDisplay = (type: PromoPopupType): void => {
  if (!isTrackedType(type)) {
    return;
  }
  writeHistory({ ...readHistory(), [type]: nowInSeconds() });
};

/**
 * This device's history as the `shown` query param, or `undefined` when there is
 * nothing to report — the server pattern-validates the param, so an empty string
 * is a 400 rather than "seen nothing".
 */
export const shownParam = (
  downloadAppCooldownHours = DOWNLOAD_APP_MAX_RETENTION_HOURS
): string | undefined => {
  const serialized = serialize(readHistory(downloadAppCooldownHours));

  return serialized.length > 0 ? serialized : undefined;
};

/**
 * One-time move of the old download-app memory into this ledger. The previous
 * flow stamped `Date.now()` (milliseconds) into the
 * `DOWNLOAD_APP_POPUP_LAST_SHOWN` cookie; here it becomes a
 * `DOWNLOAD_APP:<epochSeconds>` entry and the cookie goes away — after which
 * there is nothing left to migrate, so this is safe to call repeatedly.
 *
 * Call it only for a restaurant whose pop-ups the loyalty service owns: the
 * cookie is domain-wide, so removing it on behalf of a restaurant still running
 * the legacy pop-up would re-show that one. See
 * `useLegacyDownloadAppCookieMigration`, which is the only caller.
 */
export const migrateLegacyDownloadAppCookie = (): void => {
  if (!isBrowser()) {
    return;
  }
  try {
    const legacyMilliseconds = getCookie(
      COOKIE_ENUM.DOWNLOAD_APP_POPUP_LAST_SHOWN
    );

    if (!legacyMilliseconds) {
      return;
    }
    const history = readHistory();
    const lastShownMs = Number.parseInt(legacyMilliseconds, 10);

    if (
      history[PromoPopupType.DOWNLOAD_APP] === undefined &&
      Number.isFinite(lastShownMs) &&
      lastShownMs > 0
    ) {
      writeHistory({
        ...history,
        [PromoPopupType.DOWNLOAD_APP]: Math.floor(lastShownMs / 1000),
      });
    }
    // Gone either way: a cookie we could not read is garbage, and leaving a
    // readable one behind would let it overwrite a newer localStorage entry.
    removeCookie(COOKIE_ENUM.DOWNLOAD_APP_POPUP_LAST_SHOWN);
  } catch (error) {
    console.log("error migrating download-app pop-up cookie", error);
  }
};
